import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import Navbar from "../components/Navbar";
import { useAuth, fmtErr } from "../lib/auth";
import { toast } from "sonner";
import { User, Eye, EyeOff, ShieldCheck, ArrowLeft } from "lucide-react";

/**
 * Dedicated patient signup flow with full profile fields.
 * For OTP-only one-click signup, see /login?as=patient.
 */
export default function PatientSignup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();

  const [form, setForm] = useState({
    name: "", age: "", gender: "",
    phone: "", email: "", password: "",
  });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Forward booking deep-link params to the wizard after signup
  const _forwardQS = () => {
    const keep = ["tag", "priority", "doctor", "scan"];
    const out = new URLSearchParams();
    keep.forEach((k) => {
      const v = params.get(k);
      if (v) out.set(k, v);
    });
    const s = out.toString();
    return s ? `?${s}` : "";
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const phoneClean = form.phone.replace(/[\s\-()]/g, "");
    if (!/^\+?\d{10,15}$/.test(phoneClean)) {
      setErr("Please enter a valid mobile number with country code."); return;
    }
    if (form.password.length < 6) { setErr("Password must be at least 6 characters."); return; }
    const ageNum = Number(form.age);
    if (!form.age || isNaN(ageNum) || ageNum < 1 || ageNum > 120) {
      setErr("Please enter a valid age."); return;
    }
    if (!form.gender) { setErr("Please select your gender."); return; }

    setLoading(true);
    try {
      const payload = {
        role: "patient",
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        phone: phoneClean,
        age: ageNum,
        gender: form.gender,
      };
      await signup(payload);
      toast.success("Welcome to NeuroOpinion India!");
      nav(`/patient${_forwardQS()}`);
    } catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-xl mx-auto px-4 py-10">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4">
          <ArrowLeft className="w-4 h-4"/> Back to home
        </Link>
        <Card className="p-6 sm:p-8 border-slate-200 rounded-2xl shadow-sm">
          <div className="w-11 h-11 rounded-xl bg-teal-50 border border-teal-100 grid place-items-center text-teal-700 mb-3">
            <User className="w-6 h-6"/>
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Create your patient account</h1>
          <p className="text-sm text-slate-500 mt-1">
            Sign up with email &amp; password to book second opinions, track your cases, and download written reports.
          </p>
          <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-full">
            <ShieldCheck className="w-3 h-3"/> Encrypted &amp; private
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Patient name</Label>
              <Input
                required
                value={form.name}
                onChange={update("name")}
                placeholder="e.g. Ramesh Kumar"
                data-testid="psignup-name-input"
                className="h-11 mt-1.5 border-slate-300"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Age</Label>
                <Input
                  required
                  type="number"
                  min={1}
                  max={120}
                  inputMode="numeric"
                  value={form.age}
                  onChange={update("age")}
                  placeholder="e.g. 42"
                  data-testid="psignup-age-input"
                  className="h-11 mt-1.5 border-slate-300"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Sex</Label>
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger data-testid="psignup-gender-select" className="h-11 mt-1.5 border-slate-300">
                    <SelectValue placeholder="Select"/>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Mobile number</Label>
              <Input
                required
                inputMode="tel"
                placeholder="+91XXXXXXXXXX"
                value={form.phone}
                onChange={update("phone")}
                data-testid="psignup-phone-input"
                className="h-11 mt-1.5 border-slate-300"
              />
            </div>

            <div>
              <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Email</Label>
              <Input
                required
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="you@example.com"
                data-testid="psignup-email-input"
                className="h-11 mt-1.5 border-slate-300"
              />
            </div>

            <div>
              <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Password</Label>
              <div className="relative mt-1.5">
                <Input
                  required
                  type={showPwd ? "text" : "password"}
                  minLength={6}
                  value={form.password}
                  onChange={update("password")}
                  placeholder="••••••••"
                  data-testid="psignup-password-input"
                  className="h-11 pr-10 border-slate-300"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  aria-label={showPwd ? "Hide password" : "Show password"}
                  data-testid="psignup-password-toggle"
                >
                  {showPwd ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Minimum 6 characters.</p>
            </div>

            {err && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="psignup-error">
                {err}
              </div>
            )}

            <Button
              disabled={loading}
              type="submit"
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-full font-semibold shadow-md"
              data-testid="psignup-submit-btn"
            >
              {loading ? "Creating account…" : "Sign up"}
            </Button>
          </form>

          <div className="mt-5 pt-5 border-t border-slate-100 text-center space-y-1.5">
            <div className="text-sm text-slate-600">
              Already have an account?{" "}
              <Link to="/login?as=patient" className="text-teal-700 font-semibold hover:underline" data-testid="go-login-link">
                Log in
              </Link>
            </div>
            <div className="text-xs text-slate-500">
              Prefer mobile OTP?{" "}
              <Link to="/login?as=patient" className="text-teal-700 font-semibold hover:underline" data-testid="go-otp-link">
                One-click OTP signup
              </Link>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
